import { API, graphqlOperation } from "aws-amplify";
import { MutableRefObject, useCallback, useEffect, useRef } from "react";
import { DefaultQueryTemplate } from "../../../contexts/DashboardContext/DashboardProvider";
import { OverviewQueryTitles } from "../../../contexts/DashboardsContext";
import { onDashboard, onDashboardQuery } from "../../../graphql/subscriptions";
import defaultDashboardQueries from "../../../helpers/defaultDashboardQueryTemplates.json";
import { getDashboardQueriesFunc, getPublicDashboardQueriesFunc } from "../../../lib/helper";
import { Dashboard, DashboardQuery, DashboardQuerySource } from "../../../types/files";
import ObjectUtils from "../../../utils/ObjectUtils";
import useDashboardContext from "../useDashboardContext";

const useDashboardEffects = (userGroup: string | null, dashboardQueriesRef: MutableRefObject<DashboardQuery[]>) => {
  const { dashboard, dashboardQueries, defaultQueryTemplateValues, isPublicView,
    setDashboard, setDashboardQueries, setDefaultQueryTemplateValues } = useDashboardContext();

  const dashboardSubscriptionRef = useRef<any>(null);
  const dashboardQuerySubscriptionRef = useRef<any>(null);
  const dashboardRef = useRef<Dashboard|null>(null);

  const updateSubscribedDashboard = useCallback((updatedDashboard?: Dashboard|null) => {
    if (!updatedDashboard?.id)
      return;

    if (updatedDashboard?.id !== dashboardRef.current?.id)
      return;

    if (!ObjectUtils.equalObjects(updatedDashboard, dashboardRef.current)) {
      dashboardRef.current = updatedDashboard;
      setDashboard({ ...dashboardRef.current });
    }
  }, [setDashboard]);

  const subscribeDashboard = useCallback(async () => {
    if (dashboardSubscriptionRef.current)
      dashboardSubscriptionRef.current.unsubscribe();

    if (userGroup) {
      dashboardSubscriptionRef.current = await (API.graphql(
        graphqlOperation(onDashboard, { group: userGroup })) as any)
          .subscribe({ next: ({ value }: any) => updateSubscribedDashboard(value?.data?.onDashboard)});
    }
  }, [userGroup, updateSubscribedDashboard]);

  const updateSubscribedQuery = useCallback((updatedQuery?: DashboardQuery|null) => {
    if (!updatedQuery?.dashboardId)
      return;

    if (updatedQuery?.dashboardId !== dashboard?.id)
      return;

    const lookUpQuery = dashboardQueriesRef.current.find((query) => query.title === updatedQuery.title);

    if (!lookUpQuery) {
      dashboardQueriesRef.current.push(updatedQuery);
      setDashboardQueries(dashboardQueriesRef.current.slice());
    } else if (!ObjectUtils.equalObjects(lookUpQuery, updatedQuery)) {
      dashboardQueriesRef.current = dashboardQueriesRef.current.map((query) =>
        query.title === updatedQuery.title ? updatedQuery : query
      );
      setDashboardQueries(dashboardQueriesRef.current.slice());
    }

    if (OverviewQueryTitles.includes(updatedQuery.title)) {
      setDashboardQueries(prev => [updatedQuery, ...prev.filter(query =>
        !(query.dashboardId === updatedQuery.dashboardId && query.title === updatedQuery.title)
      )]);
    }
  // eslint-disable-next-line
  }, [dashboard]);

  const subscribeDashboardQueries = useCallback(async (id: string) => {
    if (dashboardQuerySubscriptionRef.current)
        dashboardQuerySubscriptionRef.current.unsubscribe();

    if (userGroup) {
      dashboardQuerySubscriptionRef.current = await (API.graphql(
        graphqlOperation(onDashboardQuery, { group: userGroup, dashboardId: id })) as any)
          .subscribe({ next: ({ value }: any) => updateSubscribedQuery(value?.data?.onDashboardQuery)});
    }
  }, [userGroup, updateSubscribedQuery]);

  const fetchDashboardQueries = useCallback(async () => {
    const queriesData: DashboardQuery[] = (isPublicView && dashboard!.isPublic)
      ? await getPublicDashboardQueriesFunc(dashboard!.id)
      : await getDashboardQueriesFunc(dashboard!.id);
    dashboardQueriesRef.current = [...queriesData];

    if (!!queriesData?.length) {
      if (!dashboardQueriesRef.current.find((query) => query.title === 'Location')) {
        dashboardQueriesRef.current.push({
          title: 'Location',
          order: defaultQueryTemplateValues.find(defQuery => defQuery.title === 'Location')?.order ?? 0,
          query: defaultQueryTemplateValues.find(defQuery => defQuery.title === 'Location')?.queryTemplate
            ?.replace('{dashboard_name}', 'Location') || '{dashboard_name}',
          updatedAt: new Date(),
          source: DashboardQuerySource.CRUNCHBASE,
          isRefreshing: false,
        } as DashboardQuery);
      }
    }
    setDashboardQueries(dashboardQueriesRef.current.slice());
  // eslint-disable-next-line
  }, [dashboard, dashboardQueriesRef, defaultQueryTemplateValues, isPublicView]);

  useEffect(() => {
    setDefaultQueryTemplateValues(Object.values(defaultDashboardQueries as { [key: string]: DefaultQueryTemplate }));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!!userGroup && !!dashboard)
      subscribeDashboard();
    else
      dashboardSubscriptionRef.current?.unsubscribe();

    return () => {
      dashboardSubscriptionRef.current?.unsubscribe();
    }
    // eslint-disable-next-line
  }, [userGroup, dashboard]);

  useEffect(() => {
    if (!!dashboard) {
      if (!dashboardRef.current)
        dashboardRef.current = {...dashboard};
    } else {
      dashboardRef.current = null;
    }
    // eslint-disable-next-line
  }, [dashboard]);

  useEffect(() => {
    if (!dashboard?.id) {
      dashboardQueriesRef.current = [];
      setDashboardQueries(dashboardQueriesRef.current.slice());
    } else if (!dashboardQueries.length) {
      (async () => await fetchDashboardQueries())();
    }
  // eslint-disable-next-line
  }, [dashboard?.id]);

  useEffect(() => {
    if (!!userGroup && !!dashboard)
      subscribeDashboardQueries(dashboard.id);
    else
      dashboardQuerySubscriptionRef.current?.unsubscribe();

    return () => {
      dashboardQuerySubscriptionRef.current?.unsubscribe();
    }
    // eslint-disable-next-line
  }, [userGroup, dashboard]);
};

export default useDashboardEffects;